import { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { request } from "app/network/networkManager";
import GifticonCategoryDialog, { GiftCategoryData } from "./Components/GifticonCategoryDialog";
import GiftCategoryCard from "./Components/GiftCategoryCard";
import { useMaterialUIController } from "context";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import ReorderCategoryDialog from "./Components/ReorderCategoryDialog";

function GifticonCategoryListScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [categories, setCategories] = useState<any[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openReorderDialog, setOpenReorderDialog] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<GiftCategoryData | undefined>(undefined);

    const fetchCategories = async () => {
        try {
            const response = await request("GiftCategory.GetList", {});
            if (response && response.data) {
                // Ensure sorting on fetch as well for consistent display
                const sorted = response.data.sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
                setCategories(sorted);
            }
        } catch (error) {
            console.error("Failed to fetch categories", error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleCreate = () => {
        setSelectedCategory(undefined);
        setOpenDialog(true);
    };

    const handleReorder = () => {
        setOpenReorderDialog(true);
    };

    const handleEdit = (category: any) => {
        setSelectedCategory({
            seqId: category.seqId,
            categoryCode: category.categoryCode,
            categoryName: category.categoryName,
            categoryDesc: category.categoryDesc,
            useYn: category.useYn === "Y"
        });
        setOpenDialog(true);
    };

    const handleDelete = async (seqId: number) => {
        if (window.confirm("정말 삭제하시겠습니까?")) {
            try {
                await request("GiftCategory.Delete", {
                    pathParams: { id: seqId.toString() }
                });
                fetchCategories();
            } catch (error) {
                console.error("Failed to delete category", error);
                alert("삭제 중 오류가 발생했습니다.");
            }
        }
    };

    const handleSave = async (data: GiftCategoryData) => {
        try {
            if (selectedCategory) {
                // Update
                const updateData = {
                    ...data,
                    useYn: data.useYn ? "Y" : "N"
                };
                await request("GiftCategory.Update", {
                    paramz: updateData,
                    pathParams: { id: selectedCategory.seqId!.toString() }
                });
            } else {
                // Create
                const createData = {
                    ...data,
                    useYn: data.useYn ? "Y" : "N"
                };
                await request("GiftCategory.Create", {
                    paramz: createData
                });
            }
            setOpenDialog(false);
            fetchCategories();
        } catch (error) {
            console.error("Failed to save category", error);
            alert("저장 중 오류가 발생했습니다.");
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3} pb={3}>
                <MDBox mb={3}>
                    <Card>
                        <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                            <MDTypography variant="h6" fontWeight="medium">
                                상품 카테고리 관리
                            </MDTypography>
                            <MDBox>
                                <MDButton variant="outlined" color="dark" onClick={handleReorder} sx={{ mr: 1 }}>
                                    <Icon sx={{ fontWeight: "bold" }}>sort</Icon>
                                    &nbsp;순서 변경
                                </MDButton>
                                <MDButton variant="gradient" color="info" onClick={handleCreate}>
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;신규 등록
                                </MDButton>
                            </MDBox>
                        </MDBox>
                    </Card>
                </MDBox>

                <Grid container spacing={3}>
                    {categories.map((category) => (
                        <Grid item xs={12} md={6} lg={4} key={category.seqId}>
                            <GiftCategoryCard
                                category={category}
                                onClick={() => handleEdit(category)}
                                onEdit={() => handleEdit(category)}
                                onDelete={() => handleDelete(category.seqId)}
                                darkMode={darkMode}
                            />
                        </Grid>
                    ))}
                </Grid>

                <GifticonCategoryDialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    onConfirm={handleSave}
                    initialData={selectedCategory}
                    categories={categories}
                />

                <ReorderCategoryDialog
                    open={openReorderDialog}
                    onClose={() => setOpenReorderDialog(false)}
                    onSuccess={fetchCategories}
                    categories={categories}
                    darkMode={darkMode}
                />
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
}

export default GifticonCategoryListScreen;
